import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { API_BASE_URL, MAPBOX_ACCESS_TOKEN } from '../../config';
import { sourceParam } from '../../constants/visitsSource';
import { UNIVERS_ALL } from '../../constants/visitsUnivers';
import { toDateParam } from '../../utils/dateUtils';
import { obfuscateUsername } from '../../utils/obfuscateUsername';
import { Card } from '../ui/card';

/**
 * "Carte animée" — experimental, admin-only, TeamHaven-only.
 *
 * Replays the completed visits of the period as a weather-radar trail: a visit
 * lights up at its hour and fades over the following days, so the wave of field
 * activity is read as it crosses the territory.
 *
 * Coordinates come from DTL.TH."Targets" via /api/dwh/visits/:id/geo — FCT_VISITS
 * has no geography. See backend/utils/visitsGeo.js, including why an ungeocoded
 * store is stored at (0,0) rather than NULL.
 */

// Validated with the dataviz skill's validate_palette.js under `--pairs all`: a
// map is an all-pairs form, since any two dots can land side by side. THREE slots
// is the documented ceiling there; a fourth hue fails the floors in both modes,
// so promoters past the third fold into the neutral rather than cycling a colour
// that already names someone else.
const PALETTE = {
  light: { series: ['#2a78d6', '#eb6834', '#1baf7a'], other: '#8a8f98', ring: '#ffffff' },
  dark: { series: ['#3987e5', '#d95926', '#199e70'], other: '#7b8290', ring: '#111827' },
};
const MAX_HIGHLIGHT = PALETTE.light.series.length;

const MAP_STYLE = { light: 'mapbox://styles/mapbox/light-v11', dark: 'mapbox://styles/mapbox/dark-v11' };
const FRANCE = { center: [2.213749, 46.227638], zoom: 4.9 };

const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;
// A share of the period, so a fortnight and three years both read: a fixed 3-day
// tail strobes on one and is invisible on the other.
const TRAIL_DIVISOR = 40;
const MIN_TRAIL = 12 * HOUR;
const MAX_TRAIL = 14 * DAY;

// How long the whole period takes to play, wall-clock.
const SPEEDS = [
  { id: 'slow', label: 'Lent', seconds: 90 },
  { id: 'normal', label: 'Normal', seconds: 40 },
  { id: 'fast', label: 'Rapide', seconds: 15 },
];
const DEFAULT_SPEED = SPEEDS[1];

const SOURCE_ID = 'visits-trail';
const LAYER_ID = 'visits-trail-dots';
const EMPTY_GEOJSON = { type: 'FeatureCollection', features: [] };

// Everything Mapbox and the timeline need before a point may be drawn.
const isDrawable = (p) => !!p
  && Number.isFinite(p.t)
  && Number.isFinite(p.lng) && Math.abs(p.lng) <= 180
  && Number.isFinite(p.lat) && Math.abs(p.lat) <= 90;

const readTheme = () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');

const dateTimeLabel = (ms) => new Date(ms).toLocaleString('fr-FR', {
  weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const dateLabel = (ms) => new Date(ms).toLocaleDateString('fr-FR', {
  day: '2-digit', month: 'short', year: 'numeric',
});

// French agreement: "1 visite placée", "2 visites placées".
const n = (count, singular, plural = `${singular}s`) =>
  `${count.toLocaleString('fr-FR')} ${count > 1 ? plural : singular}`;

const trailLabel = (ms) => {
  const days = ms / DAY;
  if (days < 1) return `${Math.round(ms / HOUR)} heures`;
  const rounded = Math.round(days);
  return `${rounded} jour${rounded > 1 ? 's' : ''}`;
};

// Popup content as DOM nodes, never a string: store and promoter names come from
// the warehouse, and setHTML would run whatever markup a target name contains.
const popupContent = ({ store, shopSign, promoter, at }) => {
  const root = document.createElement('div');
  root.style.font = '12px/1.45 system-ui, sans-serif';
  root.style.color = '#111';

  const title = document.createElement('strong');
  title.textContent = store;
  root.appendChild(title);
  if (shopSign) root.appendChild(document.createTextNode(` · ${shopSign}`));
  root.appendChild(document.createElement('br'));
  root.appendChild(document.createTextNode(promoter));
  root.appendChild(document.createElement('br'));

  const when = document.createElement('span');
  when.style.opacity = '0.7';
  when.textContent = dateTimeLabel(at);
  root.appendChild(when);
  return root;
};

const VisitsMap = ({
  selectedProject, startDate, endDate, fetchWithAuth, obfuscate,
  selectedSalespeople = [], selectedShopSigns = [], selectedUnivers = UNIVERS_ALL,
}) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [playing, setPlaying] = useState(false);
  const [speedId, setSpeedId] = useState(DEFAULT_SPEED.id);
  // Promoter NAMES, not indices into `users[]`. Indices are positions in one
  // payload's intern order: after a refetch, slot 0 can be a different person, so
  // an index-keyed selection silently repaints someone else in the first colour —
  // a mis-attribution on a surface that names people.
  const [highlighted, setHighlighted] = useState([]);
  // Held in state, not read during render: the theme lives on <html> and nothing
  // would re-render the legend when it flips, so its swatches would drift out of
  // step with the dots they name.
  const [themeName, setThemeName] = useState(readTheme);
  // Mirrors cursorRef for the label and the slider thumb, refreshed ~8×/s.
  // Driving React at 60fps would re-render the panel on every frame, while the
  // map itself is fed imperatively.
  const [displayTime, setDisplayTime] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);

  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const cursorRef = useRef(0);
  const layerReadyRef = useRef(false);
  const lastPublishRef = useRef(0);
  // Lets the map callbacks, which are installed once, reach the current painter.
  const renderRef = useRef(() => {});
  const appliedThemeRef = useRef(null);

  const projectId = selectedProject?.value;
  const theme = PALETTE[themeName];

  // ---------------------------------------------------------------- data ----
  useEffect(() => {
    if (!projectId) {
      // Never leave the panel spinning forever on a missing project.
      setState({ loading: false, error: null, data: null });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, error: null, data: null });

    (async () => {
      try {
        const res = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/visits/${projectId}/geo`
          + `?${sourceParam(selectedProject)}`
          + `&startDate=${toDateParam(startDate)}&endDate=${toDateParam(endDate)}`
        );
        if (!res.ok) throw new Error(`Le serveur a répondu ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        // Trust but verify. The backend already drops unplaceable rows, but this
        // is the last gate before the data reaches Mapbox: LngLatBounds THROWS on
        // a non-finite or out-of-range coordinate, which would take the whole tab
        // down rather than lose one dot. Sorting matters too — the player walks
        // the array with a moving window and mis-renders on unordered input.
        const points = (Array.isArray(data?.points) ? data.points : [])
          .filter(isDrawable)
          .sort((a, b) => a.t - b.t);
        setState({ loading: false, error: null, data: { ...data, points } });
      } catch (error) {
        if (!cancelled) setState({ loading: false, error: error.message, data: null });
      }
    })();

    return () => { cancelled = true; };
  }, [projectId, selectedProject, startDate, endDate, fetchWithAuth]);

  // Memoized so their identity is stable while `state.data` is null — otherwise
  // every render rebuilds `span`, then `renderFrame`, then tears down the player.
  const allPoints = useMemo(() => state.data?.points || [], [state.data]);
  const users = useMemo(() => state.data?.users || [], [state.data]);
  const stores = useMemo(() => state.data?.stores || [], [state.data]);
  const coverage = state.data?.coverage;

  // The viewer's global filters apply here as they do on every other tab. They
  // match the same way the other tabs do: `username` (which carries the sector)
  // and the raw shop sign — which is why the endpoint labels promoters the same.
  const filteredPoints = useMemo(() => {
    const names = selectedSalespeople.map((s) => s.value);
    const signs = selectedShopSigns.map((s) => s.value);
    const univers = selectedUnivers && selectedUnivers !== UNIVERS_ALL ? selectedUnivers : null;
    if (!names.length && !signs.length && !univers) return allPoints;
    return allPoints.filter((p) =>
      (!names.length || names.includes(users[p.u]))
      && (!signs.length || signs.includes(stores[p.s]?.shopSign))
      // A dot is one PHYSICAL visit carrying every univers it touched, so it
      // survives the filter when it touched the selected one at all.
      && (!univers || (p.univers || []).includes(univers)));
  }, [allPoints, users, stores, selectedSalespeople, selectedShopSigns, selectedUnivers]);

  const points = filteredPoints;
  // Any filter set, whether or not it happens to remove anything — the banner
  // must not imply "no filter" just because the count did not move.
  const filtering = selectedSalespeople.length > 0 || selectedShopSigns.length > 0
    || (!!selectedUnivers && selectedUnivers !== UNIVERS_ALL);

  const nameOf = useCallback(
    (userIdx) => {
      const raw = users[userIdx] || 'Inconnu';
      return obfuscate ? obfuscateUsername(raw) : raw;
    },
    [users, obfuscate]
  );

  const span = useMemo(() => {
    if (!points.length) return { from: 0, to: 0, length: 0 };
    const from = points[0].t;
    const to = points[points.length - 1].t;
    return { from, to, length: Math.max(to - from, HOUR) };
  }, [points]);

  const trailMs = useMemo(
    () => Math.min(MAX_TRAIL, Math.max(MIN_TRAIL, span.length / TRAIL_DIVISOR)),
    [span.length]
  );

  // The colour slot a promoter holds, or -1. Colour follows the person, never
  // their rank in a filtered subset.
  const slotOf = useCallback((userIdx) => highlighted.indexOf(users[userIdx]), [highlighted, users]);

  // Highlights that survive the global filters. Without this, isolating Boris and
  // then filtering to Hugo dims every dot on screen against a selection the
  // legend no longer shows — an isolation the panel silently denies, with no way
  // back except re-editing the global filter.
  const activeHighlight = useMemo(() => {
    if (!highlighted.length) return [];
    const visible = new Set(points.map((p) => users[p.u]));
    return highlighted.filter((name) => visible.has(name));
  }, [highlighted, points, users]);

  // Promoters by volume — the legend order, and the order the slots are offered in.
  const promoters = useMemo(() => {
    const counts = new Map();
    points.forEach((p) => counts.set(p.u, (counts.get(p.u) || 0) + 1));
    return [...counts.entries()]
      .map(([u, count]) => ({ u, count, name: nameOf(u) }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [points, nameOf]);

  const toggleHighlight = useCallback((userIdx) => {
    const name = users[userIdx];
    if (!name) return;
    setHighlighted((current) => {
      if (current.includes(name)) return current.filter((x) => x !== name);
      if (current.length >= MAX_HIGHLIGHT) return current;
      return [...current, name];
    });
  }, [users]);

  // Drop anyone the new period no longer contains, so a departed promoter cannot
  // keep a colour slot reserved against nothing. An empty list means "loading" or
  // "no data", neither of which should discard what the user picked.
  useEffect(() => {
    if (!users.length) return;
    setHighlighted((current) => {
      const kept = current.filter((name) => users.includes(name));
      return kept.length === current.length ? current : kept;
    });
  }, [users]);

  // ------------------------------------------------------------- painting ----
  // `immediate` is for the calls outside the animation loop (scrub, restart,
  // layer install): the 120 ms throttle exists to spare React 60 renders a
  // second, but on a one-off frame it leaves the counter contradicting the dots.
  const renderFrame = useCallback((now, immediate = false) => {
    const map = mapRef.current;
    if (!map || !layerReadyRef.current) return;
    const source = map.getSource(SOURCE_ID);
    if (!source) return;

    const palette = PALETTE[themeName];
    const features = [];
    const floor = now - trailMs;

    // Rescans from the start each frame and skips what has already faded; the
    // points are chronological, so the loop breaks as soon as it passes `now`.
    // Only a few hundred of the ~8 000 a large project-year holds are ever emitted.
    for (let i = 0; i < points.length; i += 1) {
      const p = points[i];
      if (p.t > now) break;
      if (p.t < floor) continue;

      const age = (now - p.t) / trailMs;
      const fade = 1 - age;
      const slot = slotOf(p.u);
      const dimmed = activeHighlight.length > 0 && slot === -1;

      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        properties: {
          // Size and opacity carry the figure/ground split as well as the hue, so
          // the "Autres" cloud stays background under any colour vision.
          r: (dimmed ? 2.2 : 3) + (dimmed ? 1.6 : 4.5) * fade,
          o: (dimmed ? 0.18 : 0.35) + (dimmed ? 0.22 : 0.6) * fade,
          color: activeHighlight.length === 0
            ? palette.series[0]
            : (slot >= 0 ? palette.series[slot] : palette.other),
          ring: age < 0.12 && !dimmed ? 1.4 : 0,
          ringColor: palette.ring,
          store: stores[p.s]?.name || '',
          shopSign: stores[p.s]?.shopSign || '',
          promoter: nameOf(p.u),
          at: p.t,
        },
      });
    }

    source.setData({ type: 'FeatureCollection', features });

    // Throttle the React-side mirror; the map above already moved.
    const stamp = performance.now();
    if (immediate || stamp - lastPublishRef.current > 120) {
      lastPublishRef.current = stamp;
      setDisplayTime(now);
      setVisibleCount(features.length);
    }
  }, [points, stores, trailMs, activeHighlight.length, slotOf, themeName, nameOf]);

  useEffect(() => { renderRef.current = renderFrame; }, [renderFrame]);

  // ------------------------------------------------------------ map setup ----
  // A callback ref, not an effect on a ref object: the container is created by
  // this component, so init has to fire when the node appears rather than at a
  // fixed point in the render cycle. Loading and error render as overlays over a
  // permanently mounted container, so no in-component state change can leave a
  // live map bound to a detached node. (The viewer still remounts this whole
  // subtree on a date change, so the map is rebuilt then — teardown below.)
  const attachMap = useCallback((node) => {
    if (!node) {
      layerReadyRef.current = false;
      popupRef.current?.remove();
      mapRef.current?.remove();
      mapRef.current = null;
      return;
    }
    if (mapRef.current || !MAPBOX_ACCESS_TOKEN) return;

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    const map = new mapboxgl.Map({
      container: node,
      style: MAP_STYLE[readTheme()],
      center: FRANCE.center,
      zoom: FRANCE.zoom,
      attributionControl: false,
    });
    appliedThemeRef.current = readTheme();
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;

    // Re-added on every style load, not just the first: swapping the basemap for
    // dark mode drops every runtime source and layer.
    const installLayer = () => {
      layerReadyRef.current = true;
      if (!map.getSource(SOURCE_ID)) {
        map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON });
        map.addLayer({
          id: LAYER_ID,
          type: 'circle',
          source: SOURCE_ID,
          paint: {
            'circle-radius': ['get', 'r'],
            'circle-color': ['get', 'color'],
            'circle-opacity': ['get', 'o'],
            'circle-stroke-width': ['get', 'ring'],
            'circle-stroke-color': ['get', 'ringColor'],
            'circle-stroke-opacity': 0.85,
          },
        });
      }
      // The style loads asynchronously, so the first frames are computed before
      // the layer exists — and after a theme swap, every frame since.
      renderRef.current(cursorRef.current, true);
    };

    map.on('load', installLayer);
    map.on('style.load', installLayer);

    const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 });
    popupRef.current = popup;

    map.on('mousemove', LAYER_ID, (event) => {
      const feature = event.features?.[0];
      if (!feature) return;
      map.getCanvas().style.cursor = 'pointer';
      popup
        .setLngLat(feature.geometry.coordinates)
        .setDOMContent(popupContent({
          store: feature.properties.store,
          shopSign: feature.properties.shopSign,
          promoter: feature.properties.promoter,
          at: Number(feature.properties.at),
        }))
        .addTo(map);
    });
    map.on('mouseleave', LAYER_ID, () => {
      map.getCanvas().style.cursor = '';
      popup.remove();
    });
  }, []);

  // Follow the app theme.
  useEffect(() => {
    const observer = new MutationObserver(() => setThemeName(readTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || appliedThemeRef.current === themeName) return;
    appliedThemeRef.current = themeName;
    layerReadyRef.current = false;
    // `diff: false` is load-bearing: the default diffing path serialises the
    // current style, emits removals for our runtime source and layer, and never
    // fires `style.load` — so the layer would never be re-installed and the map
    // would go permanently blank after a theme toggle.
    map.setStyle(MAP_STYLE[themeName], { diff: false });
  }, [themeName]);

  // Reset the cursor whenever the dataset changes.
  useEffect(() => {
    if (!span.length) return;
    cursorRef.current = span.from;
    setPlaying(false);
    renderRef.current(span.from, true);

    const map = mapRef.current;
    if (!map || !points.length) return;
    const bounds = points.reduce(
      (acc, p) => acc.extend([p.lng, p.lat]),
      new mapboxgl.LngLatBounds([points[0].lng, points[0].lat], [points[0].lng, points[0].lat])
    );
    map.fitBounds(bounds, { padding: 48, duration: 600, maxZoom: 9 });
  }, [span.from, span.to, span.length, points]);

  // Repaint in place when the highlight, the theme or the trail length changes.
  useEffect(() => { renderFrame(cursorRef.current, true); }, [renderFrame]);

  // ----------------------------------------------------------- the player ----
  useEffect(() => {
    if (!playing || !span.length) return undefined;
    const speed = SPEEDS.find((s) => s.id === speedId) || DEFAULT_SPEED;
    const perMs = span.length / (speed.seconds * 1000);
    let frame;
    let previous = performance.now();

    const tick = (now) => {
      const advanced = cursorRef.current + (now - previous) * perMs;
      previous = now;
      if (advanced >= span.to) {
        cursorRef.current = span.to;
        // `immediate`: this is the frame the user is left staring at. Throttled,
        // the counter would keep a stale value with nothing left to correct it.
        renderFrame(span.to, true);
        setPlaying(false);
        return;
      }
      cursorRef.current = advanced;
      renderFrame(advanced);
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    // Pausing stops mid-throttle, so publish once on the way out or the counter
    // and the slider freeze up to 120 ms behind the dots on screen.
    return () => {
      cancelAnimationFrame(frame);
      renderFrame(cursorRef.current, true);
    };
  }, [playing, speedId, span, renderFrame]);

  const scrub = useCallback((value) => {
    setPlaying(false);
    cursorRef.current = value;
    renderFrame(value, true);
  }, [renderFrame]);

  const restart = useCallback(() => {
    cursorRef.current = span.from;
    renderFrame(span.from, true);
    setPlaying(true);
  }, [span.from, renderFrame]);

  // ---------------------------------------------------------------- views ----
  const hasPoints = points.length > 0;
  const clampedTime = Math.min(Math.max(displayTime, span.from), span.to);
  const atEnd = span.length > 0 && displayTime >= span.to;

  const overlay = (() => {
    if (!MAPBOX_ACCESS_TOKEN) return 'Carte indisponible : aucun jeton Mapbox n\'est configuré.';
    if (state.loading) return null;
    if (state.error) return `La carte n'a pas pu être chargée — ${state.error}`;
    if (!projectId) return 'Sélectionnez un projet pour afficher la carte.';
    if (!hasPoints) {
      if (!allPoints.length) return 'Aucune visite géolocalisable sur cette période.';
      // "Aucune visite" would be a claim about the work, not about the map. When
      // part of the period has no coordinates, the promoter may well have visited
      // — the Détails tab will show it — so say what this map can and cannot see.
      // Cause-neutral on purpose: `missing` merges "no position" and "no usable
      // hour", and the payload reports them apart precisely because they call for
      // different fixes. Naming one of them here would attribute the wrong one.
      if (!(coverage?.missing > 0)) return 'Aucune visite ne correspond aux filtres actifs.';
      const many = coverage.missing > 1;
      return `Aucune visite géolocalisable ne correspond aux filtres actifs — ${
        n(coverage.missing, 'visite', 'visites')} de la période ${
        many ? 'ne sont pas plaçables' : "n'est pas plaçable"} sur la carte.`;
    }
    return null;
  })();

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Carte animée des visites</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Chaque point est une visite effectuée, allumée à son heure puis estompée sur {trailLabel(trailMs)}.
            </p>
          </div>
          <span className="text-[11px] uppercase tracking-wide px-2 py-1 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            Expérimental
          </span>
        </div>

        {/* Honesty first: the map can only draw the visits it can place. */}
        {coverage?.total > 0 && coverage.missing > 0 && (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 border-l-2 border-amber-400 pl-3">
            <strong className="text-gray-800 dark:text-gray-200">
              {n(coverage.located ?? 0, 'visite placée', 'visites placées')}
            </strong>
            {' '}sur {(coverage.total ?? 0).toLocaleString('fr-FR')} —
            {coverage.missingPosition > 0 && (
              <> {(coverage.missingPosition).toLocaleString('fr-FR')} dont le magasin n&apos;a pas de coordonnées dans TeamHaven</>
            )}
            {coverage.missingPosition > 0 && coverage.missingTime > 0 && ','}
            {coverage.missingTime > 0 && (
              <> {(coverage.missingTime).toLocaleString('fr-FR')} sans heure de visite exploitable</>
            )}
            {' '}n&apos;apparaissent pas.
          </p>
        )}

        {filtering && (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 border-l-2 border-blue-400 pl-3">
            {`Filtres actifs — ${n(filteredPoints.length, 'visite affichée', 'visites affichées')}`}
            {` sur ${n(allPoints.length, 'placée', 'placées')} de la période.`}
          </p>
        )}

        {state.data?.truncated && (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 border-l-2 border-amber-400 pl-3">
            Période trop large : seules les premières visites sont animées. Resserrez le filtre de dates.
          </p>
        )}

        <div className="relative">
          {/* Never swapped out for a spinner: unmounting it under a live map
              would leave the WebGL context bound to a detached node. */}
          <div
            ref={attachMap}
            className="h-[28rem] w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900"
          />
          {(state.loading || overlay) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80 dark:bg-[#1e1e1e]/80 text-center px-6">
              {state.loading
                ? <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
                : <p className="text-gray-700 dark:text-gray-300">{overlay}</p>}
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => (atEnd ? restart() : setPlaying((p) => !p))}
            disabled={!hasPoints}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium"
          >
            {atEnd ? 'Rejouer' : (playing ? 'Pause' : 'Lecture')}
          </button>

          <input
            type="range"
            min={span.from}
            max={span.to || 1}
            step={Math.max(1, Math.round((span.length || 1) / 2000))}
            value={clampedTime}
            onChange={(e) => scrub(Number(e.target.value))}
            disabled={!hasPoints}
            aria-label="Position dans la période"
            aria-valuetext={hasPoints ? dateTimeLabel(clampedTime) : 'Aucune donnée'}
            className="flex-1 min-w-[12rem] accent-blue-600 disabled:opacity-40"
          />

          <div
            role="group"
            aria-label="Vitesse de lecture"
            className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700"
          >
            {SPEEDS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSpeedId(s.id)}
                aria-pressed={speedId === s.id}
                className={`px-3 py-1.5 text-xs font-medium ${
                  speedId === s.id
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {hasPoints && (
          <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm">
            <span className="font-medium text-gray-900 dark:text-gray-100 tabular-nums">
              {dateTimeLabel(clampedTime)}
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              {n(visibleCount, 'visite')} dans la traînée
            </span>
            <span className="text-gray-600 dark:text-gray-400 tabular-nums">
              {dateLabel(span.from)} → {dateLabel(span.to)}
            </span>
          </div>
        )}
      </Card>

      {promoters.length > 0 && (
        <Card className="p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Promoteurs</h4>
            <div className="flex items-baseline gap-3">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Sélectionnez jusqu&apos;à {MAX_HIGHLIGHT} promoteurs pour les isoler ; les autres passent en gris.
              </p>
              {/* Always reachable. The cap counts every isolated promoter, including
                  those a global filter has since hidden — so without this the panel
                  can show nothing but greyed-out chips and no way to un-press. */}
              {highlighted.length > 0 && (
                <button
                  type="button"
                  onClick={() => setHighlighted([])}
                  className="text-xs font-medium text-blue-700 dark:text-blue-300 hover:underline shrink-0"
                >
                  Tout désélectionner
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {promoters.map(({ u, name, count }) => {
              const slot = slotOf(u);
              const active = slot >= 0;
              const full = !active && highlighted.length >= MAX_HIGHLIGHT;
              return (
                <button
                  key={u}
                  type="button"
                  onClick={() => toggleHighlight(u)}
                  disabled={full}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-colors ${
                    active
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-gray-100'
                      : full
                        ? 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <span
                    data-testid="promoter-swatch"
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: active ? theme.series[slot] : theme.other }}
                  />
                  {name}
                  <span className="tabular-nums text-gray-500 dark:text-gray-400">{count}</span>
                </button>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default VisitsMap;
